import React, { useEffect, useState } from "react";
import { useAppContext } from "../utils/context/app-context";
import { TextArea, StyledRadioButtons } from "../components/FormComponents";
import {
	Button,
	ContentContainer,
	TitleHeader,
	NarrowContentWrapper,
	AlertMessage,
} from "../components/UiComponents";
import { FormBeforeAfterContent } from "../components/FormComponents";
import { CourseHeader, CourseWrapper } from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";

const DailyRiskAssessmentPage = () => {
	useDocumentTitle("Daily Risk Assessment");
	const { state, api, dispatch } = useAppContext();
	const isOffline = state.offline;
	const [validationErrors, setValidationErrors] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [alreadySubmitted, setAlreadySubmitted] = useState(false);
	const [error, setError] = useState(null);
	const course = api.getCurrentCourse();
	const date = api.getCurrentDate();
	const [formData, setFormData] = useState({
		appropriate_lighting: null,
		trip_hazards: null,
		safe_training_area: null,
		matted_area_clean: null,
		mats_safely_positioned: null,
		instructions_stop_signal: null,
		instructions_trip_hazard: null,
		instructions_evacuation: null,
		instructions_procedure_for_leaving: null,
		tutor_comments: "",
	});
	const isReadOnly = useUserIsReadOnly();

	const extraContent =
		state?.forms_extra_content["daily_risk_assessment"] || {};

	useEffect(() => {
		console.log(date);
		if (date?.risk_assessment) {
			setFormData({
				...formData,
				...date.risk_assessment.answers,
			});
			setAlreadySubmitted(true);
		}
	}, []);

	const existing_form = date?.risk_assessment;

	const passFailOptions = [
		{ value: 1, label: "Pass" },
		{ value: 2, label: "Fail" },
		{ value: 3, label: "N/A" },
	];

	function handleFieldChanged(name, value) {
		setFormData({
			...formData,
			[name]: value,
		});
	}

	const PassFailField = ({ name, label }) => {
		function findValue() {
			// Check if formData has a value for this field
			if (formData[name]) {
				return formData[name];
			}

			return null;
		}

		function findValidationError() {
			// Check if validationErrors has an error for this field
			if (validationErrors && validationErrors[name]) {
				return validationErrors[name];
			}

			return null;
		}

		return (
			<StyledRadioButtons
				label={label}
				name={name}
				options={passFailOptions}
				value={findValue(name)}
				onChange={(val) => {
					handleFieldChanged(name, val);
				}}
				isPassFail={true}
				required={true}
				error={findValidationError(name)}
			/>
		);
	};

	async function handleSubmit(event) {
		event.preventDefault();
		setValidationErrors(null);
		setError(null);
		setSubmitted(false);

		// All fields in formData must be either 1 or 2 except for comments
		let errors = {};

		for (const [key, value] of Object.entries(formData)) {
			if (
				value != 1 &&
				value != 2 &&
				value != 3 &&
				key !== "tutor_comments"
			) {
				errors[key] = "This field is required";
			}
		}

		if (Object.keys(errors).length) {
			setValidationErrors(errors);
			return;
		}

		setSubmitting(true);
		const dataToPost = {
			course_id: course.id,
			date_id: date.id,
			form_data: { ...formData },
		};

		const { data, error } = await api.fetchData(
			`/forms/daily-risk-assessment`,
			"POST",
			dataToPost
		);

		if (data?.success) {
			setSubmitting(false);
			setSubmitted(true);
			dispatch({
				type: "SET_DAILY_RISK_ASSESSMENT",
				payload: {
					course_id: course.id,
					date_id: date.id,
					answers: formData,
				},
			});
		} else {
			setError(error || data?.error || "An error occurred");
			setSubmitting(false);
		}
	}

	return (
		<div className="daily-risk-assessment-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title="Daily Risk Assessment" />
					<NarrowContentWrapper>
						{submitted ? (
							<div className="form-submitted">
								<h3 className="form-submitted__title">Thank you</h3>
								<div className="form-submitted__message">
									<p>Your Daily Risk Assessment has been submitted.</p>
								</div>
							</div>
						) : (
							<>
								{alreadySubmitted && (
									<div className="mb-4">
										<AlertMessage
											type="warning"
											message="The Daily Risk Assessment for today has already been submitted, submitting another one will overwrite the existing one."
										/>
									</div>
								)}
								<FormBeforeAfterContent
									content={extraContent?.before ?? null}
								/>
								<form
									action=""
									className="form risk-assessment-form"
									onSubmit={handleSubmit}
									noValidate>
									<PassFailField
										name="appropriate_lighting"
										label="Check Appropriate Lighting"
									/>
									<PassFailField
										name="trip_hazards"
										label="Check for trip hazards/ environmental issues"
									/>
									<PassFailField
										name="safe_training_area"
										label="Ensure safe training area ie removal of hazardous materials"
									/>
									<PassFailField
										name="matted_area_clean"
										label="Ensure matted area is clean, dry and is in good condition"
									/>
									<PassFailField
										name="mats_safely_positioned"
										label="Check mats are safely positioned with no gaps"
									/>
									<div className="form__section">
										<h3 className="form__section-title">
											Deliver pre-training course instructions:
										</h3>
										<div className="form__section-body">
											<PassFailField
												name="instructions_stop_signal"
												label="(a) Stop signal"
											/>
											<PassFailField
												name="instructions_trip_hazard"
												label="(b) Trip hazard from the edges of the mats"
											/>
											<PassFailField
												name="instructions_evacuation"
												label="(c) Fire and emergency evacuation procedure"
											/>
											<PassFailField
												name="instructions_procedure_for_leaving"
												label="(d) Procedure for leaving the training area"
											/>
										</div>
									</div>
									<TextArea
										label="Tutor Comments"
										name="tutor_comments"
										value={
											formData?.tutor_comments
												? formData.tutor_comments
												: ""
										}
										onChange={(event) => {
											handleFieldChanged(
												"tutor_comments",
												event.target.value
											);
										}}
									/>
									{validationErrors && (
										<div className="mb-3">
											<AlertMessage
												type="danger"
												message="Please check the form for errors and try again."
											/>
										</div>
									)}
									{error && (
										<div className="mb-3">
											<AlertMessage type="danger" message={error} />
										</div>
									)}
									<Button
										text={
											submitting
												? "Submitting..."
												: "Submit Assessment"
										}
										type="primary"
										disabled={submitting || isReadOnly || isOffline}
										buttonType="submit"
									/>
								</form>
								<FormBeforeAfterContent
									content={extraContent?.after ?? null}
									type="after"
								/>
							</>
						)}
					</NarrowContentWrapper>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

export default DailyRiskAssessmentPage;
